import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    FacilityOperationCreatePayload,
    FacilityOperationUpdatePayload,
    IFacilityOperationDetail,
} from "@/customTypings/reference/FacilityOperation";
import { FACILITY_OPERATION_CREATE_CONFLICT } from "@/customTypings/reference/FacilityOperation";
import type { ReferenceRestoreDeletedPayload } from "@/utils/reference/referenceCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";
import { postReferenceCreate } from "@/utils/reference/referenceCreateConflict";
import type { RegLookupSelectItem } from "@/components/Forms/RegLookupSelect";
import { sortRegLookupItemsByCodeThenDescription } from "@/components/Forms/RegLookupSelect";

export const facilityOperationsService = {
    listActive,
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function listActive() {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`/api/referencedata/FacilityOperations`, requestOptions)
        .then(handleResponse)
        .then((items) => sortRegLookupItemsByCodeThenDescription(items as RegLookupSelectItem[])) as Promise<
        RegLookupSelectItem[]
    >;
}

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/referencedata/facility-operations/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IFacilityOperationDetail>;
}

function create(payload: FacilityOperationCreatePayload) {
    return postReferenceCreate<IFacilityOperationDetail>(
        `/api/referencedata/facility-operations/create`,
        payload,
        FACILITY_OPERATION_CREATE_CONFLICT.deletedRestoreAvailable
    );
}

function restoreDeletedWithCreateValues(payload: ReferenceRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/facility-operations/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IFacilityOperationDetail>;
}

function update(payload: FacilityOperationUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/facility-operations/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IFacilityOperationDetail>>;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/facility-operations/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
